import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuctionCard } from "@/components/AuctionCard";
import { AuctionDetail } from "@/components/AuctionDetail";
import { CreateAuctionDialog } from "@/components/CreateAuctionDialog";
import { fetchAuctions } from "@/lib/api";
import type { Auction } from "@/lib/types";
import { Search, Gavel, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";
import "./index.css";

const categories = [
	"All",
	"Web Development",
	"Design",
	"DevOps",
	"Backend",
	"Content",
	"Mobile",
];

export function App() {
	const [auctions, setAuctions] = useState<Auction[]>([]);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [category, setCategory] = useState("All");
	const [loading, setLoading] = useState(true);

	const loadAuctions = async () => {
		const data = await fetchAuctions();
		setAuctions(data);
		setLoading(false);
	};

	useEffect(() => {
		loadAuctions();
	}, []);

	const filtered = auctions.filter((a) => {
		const matchesSearch =
			!search ||
			a.title.toLowerCase().includes(search.toLowerCase()) ||
			a.description.toLowerCase().includes(search.toLowerCase());
		const matchesCategory = category === "All" || a.category === category;
		return matchesSearch && matchesCategory;
	});

	const activeCount = auctions.filter((a) => a.status === "active").length;
	const totalBids = auctions.reduce((sum, a) => sum + a.bids.length, 0);

	if (selectedId) {
		return (
			<TooltipProvider>
				<div className="min-h-screen bg-background">
					<header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
						<div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
							<button
								onClick={() => setSelectedId(null)}
								className="font-bold text-lg hover:opacity-80 transition-opacity"
							>
								&#8592; Back
							</button>
						</div>
					</header>
					<main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
						<AuctionDetail
							auctionId={selectedId}
							onBack={() => setSelectedId(null)}
						/>
					</main>
				</div>
			</TooltipProvider>
		);
	}

	return (
		<TooltipProvider>
			<div className="min-h-screen bg-background">
				<header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
					<div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
						<div></div>
						<CreateAuctionDialog
							onCreated={() => loadAuctions()}
						/>
					</div>
				</header>

				<main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
					{/* Hero */}
					<div className="mb-8">
						<h1 className="text-3xl font-bold tracking-tight mb-2">
							Reverse Auction Hub
						</h1>
						<p className="text-muted-foreground max-w-2xl">
							Post your service requests and let providers compete for your business.
							The best bid wins — lowest price, highest quality, best reputation.
						</p>
						<div className="flex items-center gap-4 mt-4">
							<div className="flex items-center gap-1.5 text-sm">
								<span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
								<span className="font-medium">{activeCount} active</span>
								<span className="text-muted-foreground">auctions</span>
							</div>
							<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
								<TrendingDown className="h-3.5 w-3.5" />
								{totalBids} bids placed
							</div>
						</div>
					</div>

					{/* Search & Filters */}
					<div className="flex flex-col sm:flex-row gap-3 mb-6">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								placeholder="Search auctions..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="pl-9"
							/>
						</div>
						<div className="flex items-center gap-1 flex-wrap">
							{categories.map((c) => (
								<button
									key={c}
									onClick={() => setCategory(c)}
									className={cn(
										"px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
										category === c
											? "bg-primary text-primary-foreground"
											: "bg-muted text-muted-foreground hover:text-foreground"
									)}
								>
									{c}
								</button>
							))}
						</div>
					</div>

					{/* Auction Grid */}
					{loading ? (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{[1, 2, 3, 4].map((i) => (
								<div
									key={i}
									className="h-48 bg-muted animate-pulse rounded-xl"
								/>
							))}
						</div>
					) : filtered.length === 0 ? (
						<div className="text-center py-16 text-muted-foreground">
							<Gavel className="h-12 w-12 mx-auto mb-3 opacity-30" />
							<p className="text-lg font-medium">No auctions found</p>
							<p className="text-sm mt-1">
								Try adjusting your search or filters
							</p>
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{filtered.map((auction) => (
								<AuctionCard
									key={auction.id}
									auction={auction}
									onClick={() => setSelectedId(auction.id)}
								/>
							))}
						</div>
					)}
				</main>
			</div>
		</TooltipProvider>
	);
}

export default App;
